import React, {useContext, useState} from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {green, red} from "@mui/material/colors";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {useMutationDeleteUser} from "../../../queries/user/hooks/useMutationDeleteUser";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {CircularProgress} from "@mui/material";
import {NotificationType} from "../../../constants/global";
import {ToasterContext} from "../../../components/shared/Toaster/ToasterContext";
import {PharmacyContext} from "../../../hooks/pharmacyContext";
import {PHARMACY_USERS} from "../../../queries/pharmacy/pharmacyTypeDefs";
import {useNavigate} from "react-router-dom";

interface IUser {
  user: any;
  isOwner: boolean;
  hasEditAccess: boolean;
}

export const User:React.FunctionComponent<IUser> = ({
  user,
  isOwner,
  hasEditAccess
}) => {
  const navigate = useNavigate();
  const { pharmacyId }: any = useContext(PharmacyContext);
  const {addToast} = useContext(ToasterContext);
  const [open, setOpen] = useState(false);
  const { User: { id, firstName, lastName, phone, email } } = user;

  const [deleteUser, {loading: deleteLoading}] = useMutationDeleteUser({
    variables: { where: {id: id} },
    refetchQueries: [
      {
        query: PHARMACY_USERS,
        variables: {
          where: { Pharmacy: { is: { id: { equals: +pharmacyId } } } },
          orderBy: [ { createdAt: "asc" } ]
        },
        awaitRefetchQueries: true,
      },
    ],
  });

  const handleDelete = () => {
    deleteUser().then(() => {
      setOpen(false);
      addToast({ type: NotificationType.Success, title: 'User has been successfully deleted.' });
    }).catch((error: any) => {
      setOpen(false);
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  }

  return (
    <>
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', flexDirection: 'column', position: 'relative', backgroundColor: isOwner ? 'rgba(204, 175, 155, 0.1)' : '#FFFFFF'}}>
        {!hasEditAccess && !isOwner && (
          <Box sx={{position: 'absolute', right: '8px', top: '8px'}}>
            <EditOutlinedIcon sx={{ mr: 1, cursor: 'pointer', color: green[500] }} onClick={() => navigate(`/users/${id}`)} />
            <DeleteOutlineOutlinedIcon sx={{ cursor: 'pointer', color: red[500] }} onClick={() => setOpen(true)} />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            backgroundColor: 'rgba(204, 175, 155, 0.5)',
            borderRadius: '50%',
          }}
        >
          {firstName.charAt(0).toLocaleUpperCase()}{lastName.charAt(0).toLocaleUpperCase()}
        </Box>
        <Box sx={{mt: 1, fontSize: 16, fontWeight: 600}}> {firstName} {lastName}</Box>
        <Box sx={{mt: 1, fontSize: 16, fontWeight: 600, opacity: 0.6}}>{isOwner ? 'Owner' : 'Manager'}</Box>
        <Box sx={{mt: 1, fontSize: 14, opacity: 0.9}}>{email}</Box>
        <Box sx={{mt: 1, fontSize: 14, opacity: 0.9}}>{phone}</Box>
      </Paper>
      {open && (
        <Dialog
          fullWidth
          maxWidth='sm'
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant='contained' onClick={() => handleDelete()}>
              Confirm
              {deleteLoading && <CircularProgress size={14} sx={{ml: 2}} color='inherit' />}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}